export type ForeignDomMutation = Readonly<{
  addedNodeType: "element" | "text";
  addedTag: string;
  addedAttributeNames: ReadonlyArray<string>;
  addedTextSample: string;
  parentPath: string;
  pathname: string;
}>;

const MAX_REPORTS_PER_PAGE_LOAD = 10;
const MAX_PATH_DEPTH = 6;
const MAX_TEXT_SAMPLE_LENGTH = 20;
const REACT_FIBER_KEY_PREFIX = "__reactFiber$";
const CHART_LIBRARY_SELECTOR = "[data-highcharts-chart], .highcharts-container";

function isOwnedByReact(node: Node): boolean {
  return Object.keys(node).some((key) => key.startsWith(REACT_FIBER_KEY_PREFIX));
}

function isManagedByChartLibrary(node: Node, target: Node): boolean {
  const element = node instanceof Element ? node : target;
  const closestElement = element instanceof Element ? element : element.parentElement;
  return closestElement?.closest(CHART_LIBRARY_SELECTOR) != null;
}

function describeElement(element: Element): string {
  const className = typeof element.className === "string" ? element.className.trim() : "";
  const firstClass = className.split(/\s+/)[0];
  return firstClass ? `${element.localName}.${firstClass}` : element.localName;
}

function describePath(target: Node, root: Node): string {
  const segments: Array<string> = [];
  let current: Node | null = target;
  while (current && current !== root && segments.length < MAX_PATH_DEPTH) {
    if (current instanceof Element) {
      segments.unshift(describeElement(current));
    }
    current = current.parentNode;
  }
  return segments.join(" > ");
}

function describeForeignNode(node: Node, target: Node, root: Node): ForeignDomMutation {
  const element = node instanceof Element ? node : null;
  return {
    addedNodeType: element ? "element" : "text",
    addedTag: element ? element.localName : node.nodeName,
    addedAttributeNames: element ? element.getAttributeNames() : [],
    addedTextSample: (node.textContent ?? "").slice(0, MAX_TEXT_SAMPLE_LENGTH),
    parentPath: describePath(target, root),
    pathname: window.location.pathname,
  };
}

function isForeignNode(node: Node, target: Node): boolean {
  return (
    (node instanceof Element || node instanceof Text) &&
    !isOwnedByReact(node) &&
    !isManagedByChartLibrary(node, target)
  );
}

export function observeForeignDomMutations(
  root: Node,
  report: (mutation: ForeignDomMutation) => void
): () => void {
  let reportCount = 0;

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (reportCount >= MAX_REPORTS_PER_PAGE_LOAD) {
          return;
        }
        if (isOwnedByReact(record.target) && isForeignNode(node, record.target)) {
          reportCount += 1;
          report(describeForeignNode(node, record.target, root));
        }
      }
    }
  });
  observer.observe(root, { childList: true, subtree: true });

  return () => observer.disconnect();
}
